import {DateTime} from "luxon";
import type {EBirdChecklistByFamily, EBirdChecklistBySpecies, EBirdMyDataSchema} from "ebird-mydata-reader";
import {getFamilySlug} from "../../../helpers/helpers";
import {toRootRelativeImagePath} from "../../../helpers/imagePaths";
import type {LocationEcoregionData, LocationRegionData} from "../../../helpers/types";
import type {SpeciesFamilyLink} from "../../../App";
import type {
    HotspotHabitatMetric,
    HotspotLocationSummaryItem,
    HotspotMediaItem,
    HotspotSpeciesHighlight,
    HotspotVisitSummary
} from "./types";
import {capitalizeFirstLetter} from "../../../helpers/strings";
import {getHotspotHabitatColor} from "./constants";

function getObservationTimestamp(observation: EBirdMyDataSchema) {
    if (!observation.date) return 0;

    const normalizedTime = observation.time?.trim();
    if (normalizedTime) {
        const parsedDateTime = DateTime.fromISO(`${observation.date}T${normalizedTime}`);
        if (parsedDateTime.isValid) return parsedDateTime.toMillis();
    }

    const parsedDate = DateTime.fromISO(observation.date);
    return parsedDate.isValid ? parsedDate.toMillis() : 0;
}

function getObservationVisitKey(observation: EBirdMyDataSchema) {
    return observation.submissionID ?? (observation as any).submissionId ?? `${observation.date}-${observation.time}-${observation.location}`;
}

function formatDate(date?: string) {
    if (!date) return '--';

    const parsedDate = DateTime.fromISO(date);
    return parsedDate.isValid ? parsedDate.toFormat('LLLL d, y') : date;
}

export function formatObservationDay(date?: string) {
    return formatDate(date);
}

function formatCoordinate(value?: number, positiveLabel?: string, negativeLabel?: string) {
    if (!Number.isFinite(value)) return '--';

    const direction = (value ?? 0) >= 0 ? positiveLabel : negativeLabel;
    return `${Math.abs(value as number).toFixed(4)}${direction ? ` ${direction}` : ''}`;
}

function slugifySummaryValue(value: string) {
    return value
        .trim()
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^\w]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

function formatDistance(value?: number | null) {
    if (!Number.isFinite(value ?? NaN)) return 'Not available';
    const distanceKm = (value as number) / 1000;
    return distanceKm >= 10
        ? `${Math.round(distanceKm).toLocaleString()} km`
        : `${distanceKm.toFixed(1)} km`;
}

function getEcoDataString(value: unknown) {
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return '';
}

function getEcoDataStringList(value: unknown) {
    if (Array.isArray(value)) {
        return value
            .map(item => getEcoDataString(item))
            .filter(Boolean);
    }

    const single = getEcoDataString(value);
    return single ? [single] : [];
}

function getEcoDataNumber(value: unknown) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim()) {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : undefined;
    }
    return undefined;
}

export function getLocationCoordinates(observations: EBirdMyDataSchema[]) {
    const coordinates = observations.filter(
        observation => Number.isFinite(observation.latitude) && Number.isFinite(observation.longitude)
    );

    if (!coordinates.length) return undefined;

    const totals = coordinates.reduce((accumulator, observation) => {
        accumulator.latitude += observation.latitude;
        accumulator.longitude += observation.longitude;
        return accumulator;
    }, {latitude: 0, longitude: 0});

    return {
        latitude: totals.latitude / coordinates.length,
        longitude: totals.longitude / coordinates.length
    };
}

function getSpeciesLookup(checklist: EBirdChecklistByFamily[]) {
    const speciesLookup = new Map<number, EBirdChecklistBySpecies>();

    for (const family of checklist) {
        for (const species of family.speciesList) {
            speciesLookup.set(species.taxonomicOrder, species);

            for (const alternateTaxonomicOrder of species.alternateTaxonomicOrders ?? []) {
                speciesLookup.set(alternateTaxonomicOrder, species);
            }
        }
    }

    return speciesLookup;
}

export function getHotspotVisitHistory(observations: EBirdMyDataSchema[]) {
    const visitMap = new Map<string, HotspotVisitSummary>();

    for (const observation of observations) {
        const visitKey = getObservationVisitKey(observation);
        const existing = visitMap.get(visitKey);
        const timestamp = getObservationTimestamp(observation);
        const speciesKey = observation.taxonomicOrder?.toString() || observation.commonName || observation.scientificName || '';

        if (!existing) {
            visitMap.set(visitKey, {
                visitKey,
                date: observation.date,
                time: observation.time,
                location: observation.location,
                observationCount: 1,
                speciesCount: speciesKey ? 1 : 0,
                timestamp
            });
            continue;
        }

        existing.observationCount += 1;
        existing.speciesCount += speciesKey ? 1 : 0;
        if (timestamp > existing.timestamp) {
            existing.date = observation.date;
            existing.time = observation.time;
            existing.location = observation.location;
            existing.timestamp = timestamp;
        }
    }

    return Array.from(visitMap.values()).sort((a, b) => b.timestamp - a.timestamp);
}

export function getLocationSummary(
    observations: EBirdMyDataSchema[],
    ecoData?: LocationEcoregionData,
    regionData?: LocationRegionData
): HotspotLocationSummaryItem[] {
    const coordinates = getLocationCoordinates(observations);
    const coordinateHref = coordinates
        ? `https://www.google.com/maps/search/?api=1&query=${coordinates.latitude},${coordinates.longitude}`
        : undefined;
    const ecoregionHref = ecoData?.ecoregionName
        ? `https://www.oneearth.org/ecoregions/${slugifySummaryValue(ecoData.ecoregionName)}/`
        : undefined;
    const realmHref = ecoData?.realm
        ? `https://en.wikipedia.org/wiki/${encodeURIComponent(ecoData.realm.replace(/\s+/g, '_'))}_realm`
        : undefined;

    const regionLabel = regionData?.country || regionData?.subnational1 || regionData?.subnational2
        ? [
            regionData.labels?.county || regionData.subnational2?.shortName,
            regionData.labels?.state || regionData.subnational1?.shortName,
            regionData.labels?.country || regionData.country?.shortName
        ].filter(Boolean).join(', ')
        : '';

    return [
        regionLabel ? {
            label: 'Region',
            value: regionLabel
        } : null,
        {
            label: 'Coordinates',
            value: coordinates ? `${formatCoordinate(coordinates.latitude, 'N', 'S')}, ${formatCoordinate(coordinates.longitude, 'E', 'W')}` : '--',
            href: coordinateHref
        },
        {
            label: 'Ecoregion',
            value: ecoData?.ecoregionName || 'Not available',
            href: ecoregionHref
        },
        {
            label: 'Biome',
            value: ecoData?.biomeName || 'Not available'
        },
        {
            label: 'Realm',
            value: ecoData?.realm || 'Not available',
            href: realmHref
        },
        {
            label: 'Primary habitat',
            value: getEcoDataString((ecoData as any)?.habitat_primary) || ecoData?.habitat_group || ecoData?.worldcover_label || 'Not available'
        },
        {
            label: 'Water proximity',
            value: ecoData?.water_proximity_label || formatDistance(ecoData?.water_distance_m)
        }
    ].filter((item): item is HotspotLocationSummaryItem => Boolean(item));
}

export function getHabitatSummaryText(ecoData?: LocationEcoregionData) {
    if (!ecoData) {
        return 'Habitat detail is not available for this hotspot yet.';
    }

    const primaryHabitat = getEcoDataString((ecoData as any).habitat_primary) || ecoData.habitat_group || ecoData.worldcover_label;
    const secondaryHabitats = getEcoDataStringList((ecoData as any).habitat_secondary).slice(0, 2);
    const ecoregion = ecoData.ecoregionName;
    const biome = ecoData.biomeName;

    const summaryParts = [
        primaryHabitat ? `Primary habitat is ${primaryHabitat.toLowerCase()}.` : '',
        secondaryHabitats.length ? `Secondary cover includes ${secondaryHabitats.map(habitat => habitat.toLowerCase()).join(' and ')}.` : '',
        ecoregion ? `This hotspot falls in the ${ecoregion}.` : biome ? `This hotspot falls in the ${biome}.` : ''
    ].filter(Boolean);

    return summaryParts.join(' ') || 'Habitat detail is not available for this hotspot yet.';
}

export function getHabitatMetrics(ecoData?: LocationEcoregionData): HotspotHabitatMetric[] {
    const habitatMix = (ecoData as any)?.habitat_mix;
    if (!habitatMix || typeof habitatMix !== 'object') return [];

    return Object.entries(habitatMix)
        .map(([key, value]) => ({
            key,
            label: capitalizeFirstLetter(key),
            percent: Math.round((getEcoDataNumber(value) ?? 0) * 100),
            color: getHotspotHabitatColor(key)
        }))
        .filter(metric => metric.percent > 0)
        .sort((a, b) => b.percent - a.percent);
}

export function getHabitatContextItems(ecoData?: LocationEcoregionData) {
    if (!ecoData) return [];

    const items = [
        {
            label: 'Primary habitat',
            value: getEcoDataString((ecoData as any).habitat_primary) || 'Not available'
        },
        {
            label: 'Secondary habitat',
            value: getEcoDataStringList((ecoData as any).habitat_secondary).join(', ') || 'Not available'
        },
        {
            label: 'Dominant cover',
            value: getEcoDataString((ecoData as any).habitat_dominant_worldcover_label) || ecoData.worldcover_label || 'Not available'
        },
        {
            label: 'Water context',
            value: ecoData.water_proximity_label || formatDistance(ecoData.water_distance_m)
        }
    ];

    return items.filter(item => item.value && item.value !== 'Not available');
}

export function getHotspotSpeciesHighlights(
    observations: EBirdMyDataSchema[],
    checklist: EBirdChecklistByFamily[],
    speciesImages: any,
    familyByTaxonomicOrder: Map<number, SpeciesFamilyLink>,
    parentTaxonomicOrderByTaxonomicOrder: Map<number, number>
): HotspotSpeciesHighlight[] {
    const speciesLookup = getSpeciesLookup(checklist);
    const speciesMap = new Map<number, HotspotSpeciesHighlight & {
        visitIds: Set<string>,
        firstTimestamp: number,
        lastTimestamp: number,
        liferCount: number,
        firstOfYearCount: number
    }>();

    for (const observation of observations) {
        const rawTaxonomicOrder = observation.taxonomicOrder;
        if (!Number.isFinite(rawTaxonomicOrder)) continue;

        const parentTaxonomicOrder = parentTaxonomicOrderByTaxonomicOrder.get(rawTaxonomicOrder) ?? rawTaxonomicOrder;
        const species = speciesLookup.get(parentTaxonomicOrder) ?? speciesLookup.get(rawTaxonomicOrder);
        const familyLink = familyByTaxonomicOrder.get(parentTaxonomicOrder) ?? familyByTaxonomicOrder.get(rawTaxonomicOrder);
        const timestamp = getObservationTimestamp(observation);
        const visitId = getObservationVisitKey(observation);
        const existing = speciesMap.get(parentTaxonomicOrder);

        if (!existing) {
            const commonName = species?.commonName || observation.commonName || 'Unknown species';
            speciesMap.set(parentTaxonomicOrder, {
                taxonomicOrder: parentTaxonomicOrder,
                commonName,
                scientificName: species?.scientificName || observation.scientificName,
                imageUrl: toRootRelativeImagePath(
                    species?.image?.thumb || speciesImages?.[parentTaxonomicOrder]?.thumb || speciesImages?.[rawTaxonomicOrder]?.thumb
                ) || '/assets/fallback-bird.svg',
                visitCount: 1,
                observationCount: 1,
                firstSeen: observation.date,
                lastSeen: observation.date,
                detailPath: species?.speciesCode && familyLink ? `/family-explorer/family/${getFamilySlug(familyLink.familyName)}/species/${species.speciesCode}` : undefined,
                visitIds: new Set([visitId]),
                firstTimestamp: timestamp,
                lastTimestamp: timestamp,
                liferCount: observation.isLifer ? 1 : 0,
                firstOfYearCount: observation.isFirstOfYear ? 1 : 0
            });
            continue;
        }

        existing.observationCount += 1;
        if (observation.isLifer) existing.liferCount += 1;
        if (observation.isFirstOfYear) existing.firstOfYearCount += 1;

        if (!existing.visitIds.has(visitId)) {
            existing.visitIds.add(visitId);
            existing.visitCount += 1;
        }

        if (timestamp && (existing.firstTimestamp === 0 || timestamp < existing.firstTimestamp)) {
            existing.firstTimestamp = timestamp;
            existing.firstSeen = observation.date;
        }

        if (timestamp >= existing.lastTimestamp) {
            existing.lastTimestamp = timestamp;
            existing.lastSeen = observation.date;
        }
    }

    return Array.from(speciesMap.values())
        .map(({visitIds, firstTimestamp, lastTimestamp, liferCount, firstOfYearCount, ...species}) => ({
            ...species,
            _priority: {
                liferCount,
                firstOfYearCount
            }
        }))
        .sort((a, b) => {
            if (b._priority.liferCount !== a._priority.liferCount) return b._priority.liferCount - a._priority.liferCount;
            if (b._priority.firstOfYearCount !== a._priority.firstOfYearCount) return b._priority.firstOfYearCount - a._priority.firstOfYearCount;
            if (b.visitCount !== a.visitCount) return b.visitCount - a.visitCount;
            if (b.observationCount !== a.observationCount) return b.observationCount - a.observationCount;
            return a.commonName.localeCompare(b.commonName);
        })
        .map(({_priority, ...species}) => species);
}

export function getHotspotMedia(observations: EBirdMyDataSchema[]) {
    const mediaItems = observations.reduce((items, observation) => {
        const numbers = getEcoDataString(observation.mlCatalogNumbers)
            .split(/[\s,;|]+/)
            .map(catalogNumber => catalogNumber.trim().replace(/^ML/i, ''))
            .filter(Boolean);

        for (const catalogNumber of numbers) {
            items.push({
                catalogNumber,
                observation
            });
        }

        return items;
    }, [] as HotspotMediaItem[]);

    const seenCatalogNumbers = new Set<string>();

    return mediaItems
        .sort((a, b) => getObservationTimestamp(b.observation) - getObservationTimestamp(a.observation))
        .filter(item => {
            if (seenCatalogNumbers.has(item.catalogNumber)) return false;
            seenCatalogNumbers.add(item.catalogNumber);
            return true;
        });
}

export function getHotspotMediaSummary(mediaItems: HotspotMediaItem[]) {
    const speciesSeen = new Set(mediaItems.map(item => item.observation.taxonomicOrder).filter(Number.isFinite));

    return {
        itemCount: mediaItems.length,
        speciesCount: speciesSeen.size
    };
}

export function getSpeciesSeenDateRange(speciesHighlights: HotspotSpeciesHighlight[]) {
    if (!speciesHighlights.length) return 'No species recorded here yet.';

    const firstSeen = speciesHighlights
        .map(species => species.firstSeen)
        .filter(Boolean)
        .sort()[0];
    const lastSeen = speciesHighlights
        .map(species => species.lastSeen)
        .filter(Boolean)
        .sort()
        .slice(-1)[0];

    if (!firstSeen && !lastSeen) return 'No species recorded here yet.';
    if (firstSeen && lastSeen && firstSeen !== lastSeen) return `${formatDate(firstSeen)} to ${formatDate(lastSeen)}`;
    return formatDate(firstSeen || lastSeen);
}

export function formatObservationDate(date?: string, time?: string) {
    if (!date) return '--';

    const parsedDate = DateTime.fromISO(date);
    const timeLabel = time?.trim() || 'All day';
    return `${parsedDate.isValid ? parsedDate.toFormat('LLLL d, y') : date} ${timeLabel}`;
}
